import { isEndCardMeta, isIntroCardMeta } from '@src/utility/extraElementData';

import type { ElementPreviewMeta } from '@src/utility/extraElementData';
import type { TrackType } from '@src/utility/trackTypes';

/**
 * PLAT-11816 — the asset kinds a Creator Studio timeline block can present.
 *
 * `overlay | introCard | endCard | nameCard | quoteCard` mirror
 * `CompositionType`; `graphic` is the `other` composition bucket plus any
 * composition inserted without template meta; the rest are the non-composition
 * element types that already have their own rendering.
 */
export type TimelinePreviewKind =
  | 'overlay'
  | 'introCard'
  | 'endCard'
  | 'nameCard'
  | 'quoteCard'
  | 'graphic'
  | 'shape'
  | 'text'
  | 'logo'
  | 'photo'
  | 'video'
  | 'audio';

/** Kinds that get the typed block treatment (rail + tint + glyph + label). */
export type TypedPreviewKind = Extract<
  TimelinePreviewKind,
  | 'overlay'
  | 'introCard'
  | 'endCard'
  | 'nameCard'
  | 'quoteCard'
  | 'graphic'
  | 'shape'
  | 'photo'
  | 'logo'
>;

export interface TimelinePreview {
  kind: TypedPreviewKind;
  /** Template label when known, else the kind's display name. */
  label: string;
  /** Template thumbnail, else the composition's first image child, else none. */
  thumbnailUrl?: string;
  /** `var(--timeline-<kind>)` — rail, glyph and track dot. */
  color: string;
  /** `var(--timeline-<kind>-bg)` — the block fill. */
  backgroundColor: string;
}

const KIND_TOKEN: Record<TimelinePreviewKind, string> = {
  overlay: 'overlay',
  introCard: 'intro-card',
  endCard: 'end-card',
  nameCard: 'name-card',
  quoteCard: 'quote-card',
  graphic: 'graphic',
  shape: 'shape',
  text: 'text',
  logo: 'logo',
  photo: 'photo',
  video: 'video',
  audio: 'audio',
};

const KIND_LABEL: Record<TypedPreviewKind, string> = {
  overlay: 'Overlay',
  introCard: 'Intro Card',
  endCard: 'End Card',
  nameCard: 'Name Card',
  quoteCard: 'Quote Card',
  graphic: 'Graphic',
  shape: 'Shape',
  photo: 'Photo',
  logo: 'Logo',
};

export function getTimelineKindColor(kind: TimelinePreviewKind): string {
  return `var(--timeline-${KIND_TOKEN[kind]})`;
}

export function getTimelineKindBackground(kind: TypedPreviewKind): string {
  return `var(--timeline-${KIND_TOKEN[kind]}-bg)`;
}

/** Block color for a track type; the track-name dot reads the same token. */
export function getTrackTypeColor(trackType: TrackType): string {
  switch (trackType) {
    case 'original-video':
    case 'video':
      return getTimelineKindColor('video');
    case 'audio':
    case 'linked-audio':
      return getTimelineKindColor('audio');
    case 'image':
      return getTimelineKindColor('photo');
    case 'logo':
      return getTimelineKindColor('logo');
    case 'text':
    case 'karaoke':
      return getTimelineKindColor('text');
    case 'shape':
      return getTimelineKindColor('shape');
    case 'composition':
      return getTimelineKindColor('graphic');
    default:
      return getTimelineKindColor('graphic');
  }
}

// Intro and end cards use the editor's rules, so a block never looks like a
// card the editor doesn't treat as one (or the reverse).
function kindFromMeta(meta: ElementPreviewMeta | undefined): TypedPreviewKind {
  if (isIntroCardMeta(meta)) return 'introCard';
  if (isEndCardMeta(meta)) return 'endCard';
  switch (meta?.compositionType) {
    case 'overlay':
    case 'nameCard':
    case 'quoteCard':
      return meta.compositionType;
    default:
      return 'graphic';
  }
}

/**
 * Resolve what a graphics / shape / photo / logo block should show. Returns
 * `undefined` for track types that keep their own rendering (video, audio,
 * text, captions), so a caller can fall through to the existing paths.
 */
export function resolveTimelinePreview(input: {
  trackType: TrackType;
  meta?: ElementPreviewMeta;
  /**
   * The element's own image: a photo / logo's source, or a composition's first
   * image child (today's accidental preview). Shown as a cropped tile.
   */
  imageChildUrl?: string;
}): TimelinePreview | undefined {
  const { trackType, meta, imageChildUrl } = input;
  let kind: TypedPreviewKind;
  if (trackType === 'shape') {
    kind = 'shape';
  } else if (trackType === 'image') {
    kind = 'photo';
  } else if (trackType === 'logo') {
    kind = 'logo';
  } else if (trackType === 'composition') {
    kind = kindFromMeta(meta);
  } else {
    return undefined;
  }
  const label = meta?.templateLabel?.trim() || KIND_LABEL[kind];
  const thumbnailUrl = meta?.templateThumbnailUrl || imageChildUrl || undefined;
  const preview: TimelinePreview = {
    kind,
    label,
    color: getTimelineKindColor(kind),
    backgroundColor: getTimelineKindBackground(kind),
  };
  if (thumbnailUrl) preview.thumbnailUrl = thumbnailUrl;
  return preview;
}

/**
 * The track-name dot for a graphics track. When every block on the track is
 * the same kind the dot takes that kind's color; a mixed track falls back to
 * the generic graphic green so the dot never claims a kind the track isn't.
 */
export function resolveTrackDotColor(
  trackType: TrackType,
  previews: ReadonlyArray<TimelinePreview | undefined>,
): string {
  if (trackType !== 'composition') return getTrackTypeColor(trackType);
  const kinds = new Set(previews.map(p => p?.kind ?? 'graphic'));
  if (kinds.size === 1) {
    const [only] = kinds;
    return getTimelineKindColor(only);
  }
  return getTrackTypeColor(trackType);
}
